"use client"

import { useState, useCallback } from "react"
import { useHaptic } from "@/shared/hooks/use-haptic"
import { resolveError } from "./resolver"
import type { ErrorField, ResolvedError } from "./types"
import { ErrorCode } from "./types"

type FieldErrors = Partial<Record<ErrorField, string>>

export function useFormErrors() {
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
    const [generalError, setGeneralError] = useState<string>("")
    const { trigger } = useHaptic()

    const handleError = useCallback((rawError: string | Error | unknown) => {
        const resolved = resolveError(rawError)

        const newFieldErrors: FieldErrors = {}
        let hasFieldError = false

        for (const field of resolved.fields) {
            if (field !== "general") {
                newFieldErrors[field] = resolved.message
                hasFieldError = true
            }
        }

        trigger("error")

        if (hasFieldError) {
            setFieldErrors(newFieldErrors)
            setGeneralError("")
        } else {
            setGeneralError(resolved.message)
            setFieldErrors({})
        }

        return resolved
    }, [])

    const clearErrors = useCallback(() => {
        setFieldErrors({})
        setGeneralError("")
    }, [])

    const clearFieldError = useCallback((field: ErrorField) => {
        setFieldErrors((prev) => {
            const next = { ...prev }
            delete next[field]
            return next
        })
    }, [])

    const hasError = useCallback(
        (field: ErrorField) => Boolean(fieldErrors[field]),
        [fieldErrors]
    )

    const getError = useCallback(
        (field: ErrorField) => fieldErrors[field] ?? "",
        [fieldErrors]
    )

    return {
        fieldErrors,
        generalError,
        handleError,
        clearErrors,
        clearFieldError,
        hasError,
        getError,
    }
}

export { ErrorCode }
export type { ResolvedError, ErrorField }
